import { ComponentProps, ReactNode, useEffect, useRef } from "react";
import { AppId, FCX, ID, ProductLogo } from "@datassential/platform-ui-lib";
import ExpandableSocialPostCard from "@components/SocialPostCard/ExpandableSocialPostCard";
import CopyButton from "@components/new/Table/CopyButton";
import CopyNotification from "@components/new/Table/CopyNotification";
import useNotification from "@hooks/useNotification";
import { ISocialPost } from "@models/SocialPosts";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import FoodSocialPostsFilterTag from "@pages/Food/refresh/components/FoodSocialPostsFilterTag/FoodSocialPostsFilterTag";
import "./FoodSocialExamplePosts.scss";

interface Props {
    word: string;
    /** Sort and period of the posts shown, e.g. "by engagement, past 90 days". */
    subtitle?: ReactNode;
    /** Highlighted in the posts next to `word` (the selected association). */
    association: string;
    filterLabel: string | null;
    onRemoveFilter: () => void;
    tabs: ComponentProps<typeof FoodTabs>["data"];
    activeTabId: ID;
    onTabClick: (id: ID) => void;
    posts: ISocialPost[];
    copyData: ComponentProps<typeof CopyButton>["data"];
    isLoading: boolean;
    /** Changes with every filter / sort, so the list restarts at the top. */
    postsKey: string;
}

/**
 * Example posts column of the refreshed Social section: the active filter tag, the sort tabs, copy,
 * and a vertically scrolling list of dark post cards.
 */
const FoodSocialExamplePosts: FCX<Props> = ({
    word,
    subtitle,
    association,
    filterLabel,
    onRemoveFilter,
    tabs,
    activeTabId,
    onTabClick,
    posts,
    copyData,
    isLoading,
    postsKey,
}) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const notification = useNotification();
    // Only the list is remounted per key; the scroll container stays, so bring it back to the top too.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [postsKey]);

    const hasPosts = !isLoading && posts.length > 0;

    return (
        <div className="FoodSocialExamplePosts" data-testid="blockFoodSocialExamplePosts">
            <div className="FoodSocialExamplePosts__header">
                <div className="FoodSocialExamplePosts__title" data-testid="textFoodSocialExamplePostsTitle">
                    EXAMPLE POSTS: {word}
                </div>
                <div className="FoodSocialExamplePosts__subtitle" data-testid="textFoodSocialExamplePostsSubTitle">
                    {subtitle}
                </div>
            </div>
            <div className="FoodSocialExamplePosts__filter">
                <FoodSocialPostsFilterTag
                    label={filterLabel}
                    onRemove={onRemoveFilter}
                    testId="blockFoodSocialExamplePostsFilterTag"
                />
            </div>
            <div className="FoodSocialExamplePosts__toolbar">
                <FoodTabs
                    data={tabs}
                    activeTabId={activeTabId}
                    onItemClick={onTabClick}
                    testId="blockFoodSocialExamplePostsTabs"
                />
                {/* Hidden while loading: the posts on hand still belong to the previous filter. */}
                {hasPosts && (
                    <div className="FoodSocialExamplePosts__copy" onClick={notification.handleShowNotification}>
                        <CopyButton data={copyData} testId="buttonFoodSocialExamplePostsCopy"/>
                    </div>
                )}
            </div>
            <div ref={scrollRef} className="FoodSocialExamplePosts__scroll">
                {isLoading && (
                    <div className="FoodSocialExamplePosts__state" data-testid="blockFoodSocialExamplePostsLoader">
                        <div className="spinner spinner-blink is-small">
                            <ProductLogo appId={AppId.Snap} type="regular"/>
                        </div>
                    </div>
                )}
                {!isLoading && posts.length === 0 && (
                    <div className="FoodSocialExamplePosts__state">No social posts found.</div>
                )}
                {hasPosts && (
                    <div key={postsKey} className="FoodSocialExamplePosts__list appears del-1">
                        {posts.map((post) => (
                            <ExpandableSocialPostCard
                                key={post.id}
                                post={post}
                                scrollRef={scrollRef}
                                isDark
                                word={word}
                                association={association}
                                // One class only: the card derives `${className}-inner` from it.
                                className="FoodSocialExamplePosts__card"
                                expandedClassName="FoodSocialExamplePosts__card-expanded"
                                testIdPrefix="blockFoodSocialExamplePost"
                            />
                        ))}
                    </div>
                )}
            </div>
            <CopyNotification isOpen={notification.isOpen}>
                <span>Data successfully copied</span>
            </CopyNotification>
        </div>
    );
};

export default FoodSocialExamplePosts;
